import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Grid, Typography } from "@material-ui/core";
import {
  AccountTree,
  DeviceHub,
  Http,
  Android,
  ChatBubbleOutline,
  Memory,
} from "@material-ui/icons";
import { SiOpenai } from "react-icons/si";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
  },
  channelCard: {
    borderRadius: 16,
    overflow: "hidden",
    cursor: "pointer",
    border:
      theme.mode === "light"
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.12)",
    backgroundColor: theme.palette.background.paper,
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow:
        theme.mode === "light"
          ? "0 12px 28px rgba(15, 23, 42, 0.12)"
          : "0 12px 28px rgba(0, 0, 0, 0.35)",
    },
  },
  channelCardDisabled: {
    cursor: "default",
    opacity: 0.5,
    "&:hover": {
      transform: "none",
      boxShadow: "none",
    },
  },
  channelVisual: {
    height: 120,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: { height: 100 },
  },
  channelFooter: {
    padding: theme.spacing(1.5, 1.5, 1.75),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    flex: 1,
  },
  channelName: { fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.3 },
  channelDesc: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.35,
  },
}));

const COMPACT_MARKS = {
  flowbuilder: {
    bg: "linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)",
    Icon: AccountTree,
    iconColor: "#fff",
  },
  dialogflow: {
    bg: "#f59e0b",
    Icon: Android,
    iconColor: "#fff",
  },
  n8n: {
    bg: "#ff6d5a",
    Icon: DeviceHub,
    iconColor: "#fff",
  },
  webhook: {
    bg: "#475569",
    Icon: Http,
    iconColor: "#fff",
  },
  typebot: {
    bg: "#0ea5e9",
    Icon: ChatBubbleOutline,
    iconColor: "#fff",
  },
  openai: {
    bg: "#10A37F",
    Icon: SiOpenai,
    iconColor: "#fff",
    isReactIcon: true,
  },
  chat_agent: {
    bg: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    Icon: Memory,
    iconColor: "#fff",
  },
};

const OpenAiMark = ({ size = 56 }) => (
  <Box
    style={{
      background: "#10A37F",
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <SiOpenai size={size} color="#fff" />
  </Box>
);

const CompactBrandMark = ({ brand, size = 26 }) => {
  const mark = COMPACT_MARKS[brand];
  if (!mark) return null;

  const { bg, Icon, iconColor, isReactIcon } = mark;

  return (
    <Box
      style={{
        background: bg,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isReactIcon ? (
        <Icon size={size} color={iconColor} />
      ) : (
        <Icon style={{ color: iconColor, fontSize: size }} />
      )}
    </Box>
  );
};

const ImageMark = ({ src, alt, compact = false }) => (
  <Box
    style={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: compact ? "transparent" : "#fff",
      padding: compact ? 0 : 16,
    }}
  >
    <img
      src={src}
      alt={alt}
      style={{
        width: compact ? "100%" : "100%",
        height: compact ? "100%" : "100%",
        objectFit: "contain",
        borderRadius: compact ? 12 : 0,
      }}
    />
  </Box>
);

export const IntegrationVisual = ({ visual, size = 56, variant = "gallery" }) => {
  const compactBrand = visual.compactBrand || visual.brand;

  if (variant === "compact" && visual.image) {
    return <ImageMark src={visual.image} alt="" compact />;
  }

  if (variant === "compact" && compactBrand && COMPACT_MARKS[compactBrand]) {
    return <CompactBrandMark brand={compactBrand} size={size} />;
  }

  if (visual.brand === "openai") return <OpenAiMark size={size} />;
  if (visual.brand && COMPACT_MARKS[visual.brand]) {
    return <CompactBrandMark brand={visual.brand} size={size} />;
  }
  if (visual.image) return <ImageMark src={visual.image} alt="" compact={variant === "compact"} />;
  return null;
};

const IntegrationChannelGrid = ({
  items,
  isItemUsable,
  onSelect,
  showTitle = true,
  titleKey = "connections.integrationsSection.title",
  gridItemProps = { xs: 6, sm: 4, md: 3 },
}) => {
  const classes = useStyles();

  if (!items.length) return null;

  return (
    <Box>
      {showTitle && (
        <Typography className={classes.sectionTitle}>
          {i18n.t(titleKey)}
        </Typography>
      )}
      <Grid container spacing={2}>
        {items.map((item) => {
          const usable = isItemUsable(item);
          return (
            <Grid item {...gridItemProps} key={item.id}>
              <Box
                className={`${classes.channelCard} ${
                  !usable ? classes.channelCardDisabled : ""
                }`}
                onClick={() => usable && onSelect(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && usable) onSelect(item);
                }}
              >
                <Box className={classes.channelVisual}>
                  <IntegrationVisual visual={item.visual} />
                </Box>
                <Box className={classes.channelFooter}>
                  <Typography className={classes.channelName}>
                    {i18n.t(item.labelKey)}
                  </Typography>
                  {item.descKey && (
                    <Typography className={classes.channelDesc}>
                      {i18n.t(item.descKey)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default IntegrationChannelGrid;
